import { useSearchParams } from "react-router-dom";

/**
 * useRecordMode — true when the URL has ?rec=1 or ?record=1.
 *
 * Used to hide admin chrome (SUPERADMIN badge, tier badges, admin nav
 * links, version pills, dev-only widgets) from public-facing walkthrough
 * recordings. Per-tab + per-URL; leaving the URL or removing the param
 * restores normal admin view immediately. No persistence by design — the
 * record-mode state is in the URL only.
 */
export function useRecordMode(): boolean {
  const [params] = useSearchParams();
  return params.get("rec") === "1" || params.get("record") === "1";
}

/**
 * withRecordParam — augments a path string with ?rec=1 when recording is
 * active. Idempotent (won't double-append). No-op when isRecording is
 * false or when the path already carries rec=1 / record=1.
 *
 * For consumers that render raw <a> / <Link> elements (e.g. DenSwitcherPill,
 * the "Open in recording mode →" footer link). Internal sidebar NavLinks
 * get this for free via the custom NavLink wrapper at src/components/NavLink.tsx.
 */
export function withRecordParam(path: string, isRecording: boolean): string {
  if (!isRecording || !path) return path;
  if (/[?&](rec|record)=1\b/.test(path)) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}rec=1`;
}
